import { type Component, For, createMemo, onMount, onCleanup } from "solid-js"
import { Portal } from "solid-js/web"
import type { Worker, WorkType } from "@eckersdorf/api"
import { isPolishHoliday } from "../lib/polish-holidays"

interface StatsPanelProps {
  workers: readonly Worker[]
  month: string // YYYY-MM
  schedules: Record<string, Record<string, Record<string, WorkType | null>>>
  onClose: () => void
}

export const StatsPanel: Component<StatsPanelProps> = (props) => {
  onMount(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose()
    }
    document.addEventListener("keydown", handleKeyDown)
    onCleanup(() => document.removeEventListener("keydown", handleKeyDown))
  })

  const stats = createMemo(() => {
    const [yearStr, monthStr] = props.month.split("-")
    const year = parseInt(yearStr ?? "2024", 10)
    const month = parseInt(monthStr ?? "1", 10)
    const daysInMonth = new Date(year, month, 0).getDate()

    // 1. Calculate working days (Mon-Fri minus holidays)
    const holidayDaysSet = new Set<string>()
    let saturdaysInMonth = 0
    let sundaysInMonth = 0
    let polishHolidaysInMonth = 0

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month - 1, d)
      const dayOfWeek = date.getDay()
      const dateStr = `${props.month}-${d.toString().padStart(2, "0")}`

      // Count Saturdays and Sundays
      if (dayOfWeek === 6) saturdaysInMonth++
      if (dayOfWeek === 0) sundaysInMonth++

      // Count Polish public holidays
      if (isPolishHoliday(dateStr)) {
        polishHolidaysInMonth++
      }

      // Check for holidays (if ANY worker has 'holiday' type on this day)
      let isHoliday = false
      for (const worker of props.workers) {
        const daySchedule = props.schedules[worker.id]?.[dateStr]
        if (daySchedule) {
           if (Object.values(daySchedule).some(type => type === "holiday")) {
             isHoliday = true;
             break;
           }
        }
      }

      if (isHoliday) {
        holidayDaysSet.add(dateStr)
      }
    }
    
    // Calculate working days (weekdays minus user-set holidays)
    let workingDays = 0
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month - 1, d)
      const dayOfWeek = date.getDay()
      const dateStr = `${props.month}-${d.toString().padStart(2, "0")}`
      
      if (dayOfWeek >= 1 && dayOfWeek <= 5 && !holidayDaysSet.has(dateStr)) {
        workingDays++
      }
    }

    const expectedHours = workingDays * 8

    // 2. Calculate stats for each worker
    const workerStats = props.workers.map(worker => {
      let workingHours = 0
      const daysWorked = new Set<string>()
      const vacationDays = new Set<string>()
      const sickLeaveDays = new Set<string>()
      const earlyShifts = new Set<string>()
      const lateShifts = new Set<string>()
      const saturdaysWorked = new Set<string>()
      const sundaysWorked = new Set<string>()

      const workerSchedule = props.schedules[worker.id] || {}

      for (const [dateStr, daySchedule] of Object.entries(workerSchedule)) {
       if (!daySchedule) continue

       // Count hours by type
       for (const [hourStr, type] of Object.entries(daySchedule)) {
         if (type === "seller" || type === "butcher") {
           workingHours++
           daysWorked.add(dateStr)
           
           const hour = parseInt(hourStr, 10)
           if (hour === 6) earlyShifts.add(dateStr)
           if (hour >= 20) lateShifts.add(dateStr) // "work at 20:00+"
           
           // Check weekend work
           const date = new Date(dateStr)
           const dayOfWeek = date.getDay()
           if (dayOfWeek === 6) saturdaysWorked.add(dateStr)
           if (dayOfWeek === 0) sundaysWorked.add(dateStr)
         } else if (type === "vacation") {
           vacationDays.add(dateStr)
         } else if (type === "sickLeave") {
           sickLeaveDays.add(dateStr)
         }
         // "wolne" is visual only, not counted
       }
     }

      // Vacation and sick leave count as worker's declared etat (workingHours) per day
      const etat = worker.workingHours
      const vacationHours = vacationDays.size * etat
      const sickLeaveHours = sickLeaveDays.size * etat
      const totalOrganizedHours = workingHours + vacationHours + sickLeaveHours

      return {
        worker,
        workingHours,
        vacationHours,
        sickLeaveHours,
        totalOrganizedHours,
        hoursRemaining: expectedHours - totalOrganizedHours,
        daysWorked: daysWorked.size,
        vacationDays: vacationDays.size,
        sickLeaveDays: sickLeaveDays.size,
        earlyShifts: earlyShifts.size,
        lateShifts: lateShifts.size,
        saturdaysWorked: saturdaysWorked.size,
        sundaysWorked: sundaysWorked.size
      }
    })

    return {
      workingDays,
      expectedHours,
      saturdaysInMonth,
      sundaysInMonth,
      polishHolidaysInMonth,
      workerStats
    }
  })

  return (
    <Portal>
      <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={props.onClose}>
        <div 
          class="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header Summary */}
          <div class="p-6 border-b border-gray-200 bg-gray-50 flex justify-between items-start">
            <div>
              <h2 class="text-xl font-bold text-gray-900">Podsumowanie czasu pracy</h2>
              <div class="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                <div class="flex items-center gap-2">
                  <span class="text-gray-500">Dni robocze:</span>
                  <span class="font-semibold text-gray-900">{stats().workingDays}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-gray-500">Oczekiwane godziny:</span>
                  <span class="font-semibold text-gray-900">{stats().expectedHours}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-gray-500">Liczba sobót:</span>
                  <span class="font-semibold text-yellow-700">{stats().saturdaysInMonth}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-gray-500">Liczba niedziel:</span>
                  <span class="font-semibold text-red-700">{stats().sundaysInMonth}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-gray-500">Liczba świąt:</span>
                  <span class="font-semibold text-red-700">{stats().polishHolidaysInMonth}</span>
                </div>
              </div>
            </div>
            <button 
              onClick={props.onClose}
              class="text-gray-400 hover:text-gray-500 focus:outline-none p-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Table */}
          <div class="overflow-auto flex-1 p-6">
            <table class="w-full text-sm">
              <thead class="bg-gray-50 text-gray-500 sticky top-0 z-10">
                <tr>
                  <th class="px-4 py-3 text-left font-medium rounded-l-lg">Pracownik</th>
                  <th class="px-4 py-3 text-center font-medium" title="Godziny brakujące do pełnego etatu">Brakuje</th>
                  <th class="px-4 py-3 text-center font-medium" title="Godziny pracy (sklep + lada)">Praca</th>
                  <th class="px-4 py-3 text-center font-medium" title="Godziny urlopu">Urlop</th>
                  <th class="px-4 py-3 text-center font-medium" title="Godziny chorobowego">Chorob.</th>
                  <th class="px-4 py-3 text-center font-medium" title="Suma godzin (praca + urlop + chorobowe)">Suma</th>
                  <th class="px-4 py-3 text-center font-medium" title="Liczba dni z pracą (sklep/lada)">Dni</th>
                  <th class="px-4 py-3 text-center font-medium" title="Liczba zmian porannych (od 6:00)">Rano</th>
                  <th class="px-4 py-3 text-center font-medium" title="Liczba zmian wieczornych (do 21:00)">Wiecz.</th>
                  <th class="px-4 py-3 text-center font-medium" title="Liczba przepracowanych sobót">Sob.</th>
                  <th class="px-4 py-3 text-center font-medium rounded-r-lg" title="Liczba przepracowanych niedziel">Ndz.</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <For each={stats().workerStats}>
                  {(stat) => (
                    <tr class="hover:bg-gray-50 transition-colors">
                      <td class="px-4 py-3 font-medium text-gray-900">
                        {stat.worker.lastName} {stat.worker.firstName}
                      </td>
                      <td class={`px-4 py-3 text-center font-bold ${stat.hoursRemaining < 0 ? 'text-red-600' : 'text-gray-700'}`}>
                        {stat.hoursRemaining}
                      </td>
                      <td class="px-4 py-3 text-center text-gray-600">{stat.workingHours}</td>
                      <td class="px-4 py-3 text-center text-yellow-600" title={`${stat.vacationDays} dni × ${stat.worker.workingHours}h etat`}>{stat.vacationHours}</td>
                      <td class="px-4 py-3 text-center text-orange-600" title={`${stat.sickLeaveDays} dni × ${stat.worker.workingHours}h etat`}>{stat.sickLeaveHours}</td>
                      <td class="px-4 py-3 text-center font-semibold text-gray-700">{stat.totalOrganizedHours}</td>
                      <td class="px-4 py-3 text-center text-blue-600 font-medium">{stat.daysWorked}</td>
                      <td class="px-4 py-3 text-center text-gray-600">{stat.earlyShifts}</td>
                      <td class="px-4 py-3 text-center text-gray-600">{stat.lateShifts}</td>
                      <td class="px-4 py-3 text-center text-gray-600">{stat.saturdaysWorked}</td>
                      <td class="px-4 py-3 text-center text-gray-600">{stat.sundaysWorked}</td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Portal>
  )
}
